import type { z } from 'zod';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { formatStreamLabel, formatStreamShortLabel } from './formatters';
import { DBStreamSchema, StreamSchema } from './schemas';

type DBStream = z.infer<typeof DBStreamSchema>;
type Stream = z.infer<typeof StreamSchema>;

export const StreamCodecConfig: TransformerConfig<typeof StreamSchema, typeof DBStreamSchema> = {
  name: 'Stream',
  clientSchema: StreamSchema,
  dbSchema: DBStreamSchema,
  transformers: [
    createRenameTransformer<typeof StreamSchema, typeof DBStreamSchema>('id', 'streamId'),
    createRenameTransformer<typeof StreamSchema, typeof DBStreamSchema>('courseId', 'course'),
    createRenameTransformer<typeof StreamSchema, typeof DBStreamSchema>(
      'orderedLessonTemplateCodes',
      'orderedLessons'
    ),

    // label and shortLabel restate the cohort fields, so they are composed on
    // the way out and never written back
    createTransformer<typeof StreamSchema, typeof DBStreamSchema>({
      encoder: {
        sourceFields: ['label', 'shortLabel'] as const,
        encode: () => ({}),
      },
      decoder: {
        sourceFields: ['gradYear', 'type', 'groupName', 'groupCode'] as const,
        decode: parts => {
          const named = parts as Pick<DBStream, 'gradYear' | 'type' | 'groupName' | 'groupCode'>;

          return {
            ...named,
            label: formatStreamLabel(named),
            shortLabel: formatStreamShortLabel(named),
          };
        },
      },
    }),

    // slackChannels: [{ slackChannelId, label }] ↔ slackIds: { slackChannelId: { label } }
    createTransformer<typeof StreamSchema, typeof DBStreamSchema>({
      encoder: {
        sourceFields: ['slackChannels'] as const,
        encode: ({ slackChannels }) => ({
          slackIds: Object.fromEntries(
            (slackChannels as Stream['slackChannels']).map(channel => [
              channel.slackChannelId,
              { label: channel.label },
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['slackIds'] as const,
        decode: ({ slackIds }) => ({
          slackChannels: Object.entries(slackIds as DBStream['slackIds']).map(
            ([slackChannelId, data]) => ({ slackChannelId, label: data.label })
          ),
        }),
      },
    }),

    // basePermissionIds: [permissionId] ↔ basePermissions: { permissionId: {} }
    createTransformer<typeof StreamSchema, typeof DBStreamSchema>({
      encoder: {
        sourceFields: ['basePermissionIds'] as const,
        encode: ({ basePermissionIds }) => ({
          basePermissions: Object.fromEntries(
            (basePermissionIds as Stream['basePermissionIds']).map(permissionId => [
              permissionId,
              {},
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['basePermissions'] as const,
        decode: ({ basePermissions = {} }) => ({
          basePermissionIds: Object.keys(basePermissions),
        }),
      },
    }),

    // active: boolean ↔ active: 'true' | 'false'
    createTransformer<typeof StreamSchema, typeof DBStreamSchema>({
      encoder: {
        sourceFields: ['active'] as const,
        encode: ({ active }) => ({
          active: (active as Stream['active']) ? 'true' : 'false',
        }),
      },
      decoder: {
        sourceFields: ['active'] as const,
        decode: ({ active }) => ({
          active: (active as DBStream['active']) === 'true',
        }),
      },
    }),
  ],
};

export const encodeStreamToDb = createEncodeToDbFn(StreamCodecConfig);
export const decodeStreamFromDb = createDecodeFromDbFn(StreamCodecConfig);
